/**
 * admin-brief.ts — Editorial copy layer for the /admin portal standfirst.
 *
 * The /admin portal aggregates three admin queues — pending orgs, pending
 * users (those still awaiting the welcome email), and tenants needing
 * consent — into one brief at the top of the page. The voice rules mirror the
 * rest of Guardian:
 *
 *   - Six-word-or-fewer verdict. The admin portal draws none (verdicts live
 *     on the Guardian Score tile only, D22).
 *   - Named subjects beat row counts ("Eight people need attention" beats
 *     "8 users non-compliant").
 *   - No em-dashes in headlines. Period-then-newline reads cleaner and
 *     avoids the AI-coded em-dash tell.
 *
 * This composer is non-seeded: the admin queues are a small, action-driven
 * state, so deterministic copy (one verdict per state) reads less noisy than
 * rotating templates. Only the leading domain changes when counts shift.
 *
 * This composer just returns the text; AdminPortalHeader renders the
 * action-first fields through BriefHero.
 *
 * Action-first fields (blueprint §6.2 row 11): `headline` is no longer the
 * verdict. It is the h1 of the brief contract (§4.7), so it now leads with the
 * action for the leading queue ("Decide on the 2 pending organizations.");
 * `verdict` keeps the six-word verdict. `lede` names the leading queue in bold
 * and the other non-empty queues after it.
 */

import type { ActionBriefFields } from './brief-shape'

/* -------------------------------------------------------------------------- */
/* Public types                                                                */
/* -------------------------------------------------------------------------- */

export interface AdminQueueCounts {
  /** Orgs with Status='PendingApproval' awaiting an admin decision. */
  pendingOrgs: number
  /** Users with `welcomeEmailSentAt === null` — onboarding hasn't completed. */
  pendingUsers: number
  /** Tenants needing the Guardian app to be granted consent. */
  tenantsNeedingConsent: number
}

export interface AdminBrief {
  /**
   * Action-first h1 (blueprint §6.2 row 11), e.g. "Decide on the 2 pending
   * organizations." Until run 2a this mirrored `verdict`; the verdict now
   * lives only in `verdict`.
   */
  headline: string
  /** Never set by this composer. */
  emphasis?: ActionBriefFields['emphasis']
  /** One sentence, one `**bold**` run naming the leading queue and its count. */
  lede: string
  /** Six-word verdict. Not drawn (D22). */
  verdict: string
  /** Byline footer: "By Guardian · {timestamp} · Platform scope". */
  byline: string
  /** Pass-through of the input counts for the rendering layer's score chips. */
  scoreBreakdown: { orgs: number; people: number; tenants: number }
}

export interface ComposeAdminBriefOptions {
  /** Override "now" — primarily for deterministic tests. */
  asOfDate?: Date
}

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Domain identifier used internally to route the verdict picker.
 * - `orgs`     — pendingOrgs queue
 * - `people`   — pendingUsers queue (rendered as "people", never "users")
 * - `tenants`  — tenantsNeedingConsent queue
 */
type AdminDomain = 'orgs' | 'people' | 'tenants'

/**
 * Verdict strings. Each is ≤6 words; templates take a single `{count}` token
 * that gets filled from `COUNT_WORD` (word-form for 2-9, the literal "One"
 * for singular, omitted for the "several" branch).
 *
 * The pool stays small on purpose. Admins look at this brief many times a day
 * — rotating variants would read noisy, not advisory.
 */
export const ADMIN_VERDICTS = {
  clean: 'All clear. Nothing pending.',

  orgsOne: 'One org needs a decision.',
  orgsFew: '{count} orgs need a decision.',
  orgsMany: 'Several orgs need attention.',

  peopleOne: 'One person needs attention.',
  peopleFew: '{count} people need attention.',
  peopleMany: 'Several people need attention.',

  tenantsOne: 'One tenant needs consent.',
  tenantsFew: '{count} tenants need consent.',
  tenantsMany: 'Several tenants need consent.',

  allThree: 'Several items need attention.',
} as const

/** Words for small counts so verdicts read as prose, not digits. */
const COUNT_WORD: Record<number, string> = {
  2: 'Two',
  3: 'Three',
  4: 'Four',
  5: 'Five',
  6: 'Six',
  7: 'Seven',
  8: 'Eight',
  9: 'Nine',
}

/**
 * Action-first h1 per leading queue (blueprint §6.2 row 11). Counts render as
 * digits, as in the Carbon mockups; the singular forms drop the count.
 */
const HEADLINE_ACTION = {
  ORGS_ONE: 'Decide on the pending organization.',
  ORGS: 'Decide on the {n} pending organizations.',
  TENANTS_ONE: 'Grant consent for the pending tenant.',
  TENANTS: 'Grant consent for the {n} tenants.',
  PEOPLE_ONE: 'Review the person waiting for access.',
  PEOPLE: 'Review the {n} people waiting for access.',
  CLEAN: 'Nothing to review.',
}

/**
 * Lede phrases per queue. The leading queue is the bold head; the other
 * non-empty queues follow as unbolded clauses (only the non-zero domains).
 */
const LEDE = {
  ORGS_ONE: '1 organization is waiting for approval',
  ORGS: '{n} organizations are waiting for approval',
  PEOPLE_ONE: '1 person needs access',
  PEOPLE: '{n} people need access',
  TENANTS_ONE: '1 tenant needs consent',
  TENANTS: '{n} tenants need consent',
  CLEAN: '**Every admin queue is clear**.',
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** Format a readable timestamp for the byline: "19 May 2026, 14:00". */
function formatBylineTimestamp(now: Date): string {
  const date = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(now)
  const time = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(now)
  return `${date}, ${time}`
}

/** Count the words in a verdict — used by the ≤6-word regression guard. */
export function verdictWordCount(verdict: string): number {
  return verdict.split(/\s+/).filter(Boolean).length
}

/** Coerce any numeric input to a non-negative integer (loading / NaN → 0). */
function safeCount(n: unknown): number {
  if (typeof n !== 'number' || !Number.isFinite(n) || n < 0) return 0
  return Math.floor(n)
}

/**
 * Word-form lookup for verdict templates. Numbers outside 2-9 fall back to
 * the digit form so we never silently lose information — but the caller
 * routes 10+ counts to the "many" branch which doesn't use this helper.
 */
function wordForCount(n: number): string {
  return COUNT_WORD[n] ?? String(n)
}

/**
 * Decide which domain owns the verdict. When more than one domain is non-zero,
 * the one with the highest count leads. Ties resolve in a stable, documented
 * order: orgs → people → tenants. The "all three non-zero" case is detected
 * separately by the caller and routes through `ADMIN_VERDICTS.allThree`.
 */
function pickLeadingDomain(counts: {
  orgs: number
  people: number
  tenants: number
}): AdminDomain {
  const ordered: Array<{ domain: AdminDomain; count: number }> = [
    { domain: 'orgs', count: counts.orgs },
    { domain: 'people', count: counts.people },
    { domain: 'tenants', count: counts.tenants },
  ]
  // Stable sort: highest count wins; ties keep the array order above.
  ordered.sort((a, b) => b.count - a.count)
  return ordered[0].domain
}

/** Build a single-domain verdict string (already capped at six words). */
function verdictForDomain(domain: AdminDomain, count: number): string {
  if (count <= 0) return ADMIN_VERDICTS.clean
  if (count === 1) {
    if (domain === 'orgs') return ADMIN_VERDICTS.orgsOne
    if (domain === 'people') return ADMIN_VERDICTS.peopleOne
    return ADMIN_VERDICTS.tenantsOne
  }
  if (count <= 9) {
    const word = wordForCount(count)
    if (domain === 'orgs') return ADMIN_VERDICTS.orgsFew.replace('{count}', word)
    if (domain === 'people') return ADMIN_VERDICTS.peopleFew.replace('{count}', word)
    return ADMIN_VERDICTS.tenantsFew.replace('{count}', word)
  }
  if (domain === 'orgs') return ADMIN_VERDICTS.orgsMany
  if (domain === 'people') return ADMIN_VERDICTS.peopleMany
  return ADMIN_VERDICTS.tenantsMany
}

/* -------------------------------------------------------------------------- */
/* Action-first headline + lede (blueprint §6.2 row 11)                       */
/* -------------------------------------------------------------------------- */

function countFor(domain: AdminDomain, counts: { orgs: number; people: number; tenants: number }): number {
  return domain === 'orgs' ? counts.orgs : domain === 'people' ? counts.people : counts.tenants
}

function actionHeadline(domain: AdminDomain, count: number): string {
  if (domain === 'orgs') return count === 1 ? HEADLINE_ACTION.ORGS_ONE : HEADLINE_ACTION.ORGS.replace('{n}', String(count))
  if (domain === 'people') {
    return count === 1 ? HEADLINE_ACTION.PEOPLE_ONE : HEADLINE_ACTION.PEOPLE.replace('{n}', String(count))
  }
  return count === 1 ? HEADLINE_ACTION.TENANTS_ONE : HEADLINE_ACTION.TENANTS.replace('{n}', String(count))
}

function ledePhrase(domain: AdminDomain, count: number): string {
  if (domain === 'orgs') return count === 1 ? LEDE.ORGS_ONE : LEDE.ORGS.replace('{n}', String(count))
  if (domain === 'people') return count === 1 ? LEDE.PEOPLE_ONE : LEDE.PEOPLE.replace('{n}', String(count))
  return count === 1 ? LEDE.TENANTS_ONE : LEDE.TENANTS.replace('{n}', String(count))
}

/**
 * The lede: the leading queue in bold, then the other non-empty queues in the
 * stable orgs → people → tenants order, as one sentence.
 */
function buildLede(leading: AdminDomain, counts: { orgs: number; people: number; tenants: number }): string {
  const head = `**${ledePhrase(leading, countFor(leading, counts))}**`
  const rest = (['orgs', 'people', 'tenants'] as const)
    .filter((d) => d !== leading && countFor(d, counts) > 0)
    .map((d) => ledePhrase(d, countFor(d, counts)))
  if (rest.length === 0) return `${head}.`
  if (rest.length === 1) return `${head}, and ${rest[0]}.`
  return `${head}, ${rest[0]}, and ${rest[1]}.`
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose the /admin portal brief from the three admin-queue counts.
 *
 * Inputs are coerced defensively: negative or non-finite values are treated
 * as zero so the brief degrades to "All clear" rather than throwing on a
 * malformed API response. The composer is pure and synchronous; callers can
 * memo on `(pendingOrgs, pendingUsers, tenantsNeedingConsent, asOfDate)`.
 */
export function composeAdminBrief(
  counts: AdminQueueCounts,
  opts: ComposeAdminBriefOptions = {},
): AdminBrief {
  const now = opts.asOfDate ?? new Date()
  const orgs = safeCount(counts?.pendingOrgs)
  const people = safeCount(counts?.pendingUsers)
  const tenants = safeCount(counts?.tenantsNeedingConsent)
  const normalized = { orgs, people, tenants }

  const activeDomainCount =
    (orgs > 0 ? 1 : 0) + (people > 0 ? 1 : 0) + (tenants > 0 ? 1 : 0)

  let verdict: string
  if (activeDomainCount === 0) {
    verdict = ADMIN_VERDICTS.clean
  } else if (activeDomainCount === 3) {
    // All three non-zero — single mixed verdict, no domain leads.
    verdict = ADMIN_VERDICTS.allThree
  } else {
    // One or two active domains — lead with the highest-count domain. When
    // only one is active, `pickLeadingDomain` still returns the right answer
    // because the zero-count domains can never win the sort.
    const leading = pickLeadingDomain(normalized)
    const leadingCount =
      leading === 'orgs' ? orgs : leading === 'people' ? people : tenants
    verdict = verdictForDomain(leading, leadingCount)
  }

  // The h1 and lede lead with the highest-count queue (ties: orgs → people →
  // tenants), including when all three are open and the verdict stays generic.
  const leading = activeDomainCount === 0 ? undefined : pickLeadingDomain(normalized)

  return {
    headline: leading ? actionHeadline(leading, countFor(leading, normalized)) : HEADLINE_ACTION.CLEAN,
    lede: leading ? buildLede(leading, normalized) : LEDE.CLEAN,
    verdict,
    byline: `By Guardian · ${formatBylineTimestamp(now)} · Platform scope`,
    scoreBreakdown: normalized,
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Internal helpers exposed for the unit test file. Not part of the public
 * runtime API — do not import from app code.
 */
export const __TEST_ONLY__ = {
  ADMIN_VERDICTS,
  HEADLINE_ACTION,
  LEDE,
  COUNT_WORD,
  wordForCount,
  pickLeadingDomain,
  verdictForDomain,
  safeCount,
}
